import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import { Button } from '@/components/ui/button';
import { Toaster } from 'sonner';
import ClockIconSolid from '@/components/icons/ClockIconSolid';
import EDateText from '@/components/everridge/EDateText';
import NotificationsTypeDetails from './NotificationsTypeDetails';
import ESearchInput from '@/components/everridge/ESearchInput';
import ETable from '@/components/everridge/table/ETable';
import NotificationsDetailsColumns from './NotificationsDetailsColumns';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { useNotificationDetailsApi } from '@/features/notifications/hooks/useNotificationDetailsApi';
import useEmblaCarousel from 'embla-carousel-react';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { Roles } from '@/@types/auth.types';
import EAvatar from '@/components/everridge/EAvatar';

enum NotificationsDetailsButtons {
	All,
	Read,
	Unread,
}

type Props = {
	id?: string;
	role?: Roles;
	onClose: () => void;
};

const NotificationsDetails: FC<Props> = ({ id, role, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);
	const [activeButton, setActiveButton] = useState(
		NotificationsDetailsButtons.All,
	);
	const [searchValue, setSearchValue] = useState('');

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();
	const columns = NotificationsDetailsColumns();

	const [emblaRef, emblaApi] = useEmblaCarousel({
		align: 'center',
		dragFree: true,
	});

	const { notificationDetailsData } = useNotificationDetailsApi({ id });

	if (!notificationDetailsData) return null;

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	const handleClickButton = (index: number) => {
		setActiveButton(index);
		if (emblaApi) {
			emblaApi.scrollTo(index);
		}
	};

	const NotificationsDetailsButtonsList = [
		{
			name: t('main.notifications.components.details.buttons.all'),
			quantity: notificationDetailsData.recipients?.length,
		},
		{
			name: t('main.notifications.components.details.buttons.read'),
			quantity: notificationDetailsData.recipients?.filter(
				(recipient) => recipient.isRead,
			).length,
		},
		{
			name: t('main.notifications.components.details.buttons.unread'),
			quantity: notificationDetailsData.recipients?.filter(
				(recipient) => !recipient.isRead,
			).length,
		},
	];

	const filteredList =
		activeButton === NotificationsDetailsButtons.All
			? notificationDetailsData.recipients
			: notificationDetailsData.recipients?.filter((notification) => {
					return activeButton === NotificationsDetailsButtons.Read
						? notification.isRead
						: !notification.isRead;
			  });

	return (
		<Sheet
			open={isOpen}
			onOpenChange={handleClose}>
			<SheetContent
				className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
				<SheetHeader>
					<SheetDescription />
					<SheetTitle>
						{!isMobile && (
							<SheetClose
								className={cn(
									'flex items-center justify-center cursor-pointer',
								)}>
								<MoveLeftIcon />
							</SheetClose>
						)}
						{t('main.notifications.components.details.title')}
						{isMobile && (
							<SheetClose className='size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full'>
								<Cancel_01_Icon />
							</SheetClose>
						)}
					</SheetTitle>
				</SheetHeader>
				<div className='h-full p-6 flex flex-col gap-6 bg-popover-30 select-none'>
					<div className='flex justify-between'>
						<div className='flex gap-1.5 text-sm'>
							<ClockIconSolid />
							<EDateText date={notificationDetailsData.sent} />
						</div>
						<NotificationsTypeDetails
							type={notificationDetailsData.isRequired}
						/>
					</div>
					<div className='flex flex-col gap-1.5'>
						<h3
							className={cn(
								'text-lg font-medium break-all',
								isMobile && 'text-base',
							)}>
							{notificationDetailsData.title}
						</h3>
						<p className='text-sm text-popover-800 break-all'>
							{notificationDetailsData.message}
						</p>
					</div>
					{role === Roles.admin && (
						<div className='flex flex-col gap-4 overflow-hidden'>
							<h3 className='text-lg font-medium'>
								{t(
									'main.notifications.components.details.list',
								)}
							</h3>
							<div className='flex gap-2'>
								<div
									className={cn(
										'-mx-6 px-6 overflow-hidden',
										isMobile && '-mx-4 px-4',
									)}
									ref={emblaRef}>
									<div className='flex gap-2 touch-pan-y'>
										{NotificationsDetailsButtonsList.map(
											(btn, index) => (
												<Button
													key={index}
													variant={
														activeButton === index
															? 'default'
															: 'outline'
													}
													className={cn(
														`h-9 px-3.5 py-2 hover:shadow-none ${
															activeButton ===
															index
																? ''
																: 'hover:bg-popover-50'
														} `,
													)}
													onClick={() =>
														handleClickButton(index)
													}>
													{`${btn.name} (${btn.quantity})`}
												</Button>
											),
										)}
									</div>
								</div>
							</div>
							<ESearchInput
								value={searchValue}
								setValue={setSearchValue}
								className='w-full'
								placeholder={t(
									'main.notifications.components.details.form.search.placeholder',
								)}
							/>
							{!isMobile && (
								<div className='pb-20 overflow-y-scroll no-scrollbar'>
									<ETable
										data={filteredList ?? []}
										columns={columns}
										search={searchValue}
									/>
								</div>
							)}
							{isMobile &&
								filteredList?.map((owner) => (
									<div
										key={owner.userId}
										className='p-4 flex items-center gap-4 text-sm'>
										<EAvatar
											url={owner.avatar}
											className={'size-8'}
										/>
										<span className='font-medium'>
											{owner.userName}
										</span>
										<span className='ml-auto text-popover-600'>
											{owner.isRead && owner.readAt ? (
												<EDateText
													date={owner.readAt}
												/>
											) : (
												t(
													'main.notifications.table.header.unread',
												)
											)}
										</span>
									</div>
								))}
						</div>
					)}
				</div>
				<Toaster />
			</SheetContent>
		</Sheet>
	);
};

export default NotificationsDetails;
